import type React from 'react';
import {
  useInfiniteQuery,
  useQuery,
  keepPreviousData,
} from '@tanstack/react-query';
import { collapseOptionsByLabel } from './collapseOptionsByLabel';
import {
  type FilterOptions,
  type FilterOption,
  type FilterOptionsParams,
  type DynamicFilterOptions,
  type DynamicFilterOptionsPage,
  type DynamicFilterOptionItem,
  type DynamicFilterOptionsResult,
  type StaticFilterOptions,
  type FilterTooltipOptions,
  FilterOptionTypes,
} from '../types';

interface UseFilterOptionsResult {
  options: FilterOption[];
  /** Total number of options available (before pagination/filtering) */
  total?: number;
  isLoading: boolean;
  isError: boolean;
  refetch?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  defaultRender?: (
    option: FilterOption,
    isSelected: boolean
  ) => React.ReactNode;
  tooltipOptions?: FilterTooltipOptions;
}

const emptyAsync = async () => [];

const isDynamicFilterOptions = (
  options?: FilterOptions
): options is DynamicFilterOptions =>
  options?.type === FilterOptionTypes.DYNAMIC;

const isStaticFilterOptions = (
  options?: FilterOptions
): options is StaticFilterOptions => options?.type === FilterOptionTypes.STATIC;

const extractOptionItems = (
  data?: DynamicFilterOptionsResult | DynamicFilterOptionsPage
): Array<string | DynamicFilterOptionItem> => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'object' && Array.isArray(data.options)) {
    return data.options;
  }

  return [];
};
/**
 * Hook to handle both static and dynamic filter options.
 * Static options are returned immediately.
 * Dynamic options are fetched via React Query with caching.
 *
 * For dynamic options:
 * - queryFn returns string[]
 * - labelFormatter (optional) converts values to labels
 * - Returns FilterOption[] with value and label
 * - params are passed to queryFn and included in queryKey
 */
export function useFilterOptions(
  filterOptions?: FilterOptions,
  params?: FilterOptionsParams
): UseFilterOptionsResult {
  const isDynamic = isDynamicFilterOptions(filterOptions);
  const isStatic = isStaticFilterOptions(filterOptions);
  const dynamicFilterOptions = isDynamic ? filterOptions : undefined;
  const staticFilterOptions = isStatic ? filterOptions : undefined;
  const pagination = dynamicFilterOptions?.pagination ?? null;

  // KIR-3769: when a column opts into client-side search, the type-ahead is
  // NOT sent to the server — the full option set is fetched once (search
  // stripped from both the query key and the queryFn params, so keystrokes
  // reuse the same cache entry) and filtered locally by label further down.
  const clientSideSearch = dynamicFilterOptions?.clientSideSearch === true;
  const effectiveParams: FilterOptionsParams = clientSideSearch
    ? { ...(params || {}), search: undefined }
    : params || {};

  // Default to enabled if not explicitly set (backwards compatibility)
  // Pass enabled: false to defer loading until dropdown opens
  const isEnabled = params?.enabled !== false;

  const queryKey = dynamicFilterOptions
    ? dynamicFilterOptions.getQueryKey(effectiveParams)
    : [];

  const queryEnabled = !!dynamicFilterOptions && !pagination && isEnabled;
  const queryFn = queryEnabled
    ? () => dynamicFilterOptions.queryFn(effectiveParams)
    : emptyAsync;
  const queryStaleTime =
    dynamicFilterOptions?.queryConfig?.staleTime ?? Infinity;
  const queryCacheTime =
    dynamicFilterOptions?.queryConfig?.cacheTime ?? Infinity;

  const query = useQuery({
    queryKey: dynamicFilterOptions ? queryKey : [],
    queryFn,
    enabled: queryEnabled,
    staleTime: queryStaleTime,
    gcTime: queryCacheTime,
    placeholderData: keepPreviousData,
  });

  const pagedQueryEnabled = !!dynamicFilterOptions && !!pagination && isEnabled;
  const pagedQueryKey = pagedQueryEnabled ? queryKey : [];
  const pagedQueryFn = pagedQueryEnabled
    ? ({ pageParam }: { pageParam?: number }) =>
        dynamicFilterOptions.queryFn({
          // Use effectiveParams so `clientSideSearch` (search stripped) stays
          // consistent across the paginated + non-paginated paths. Identical to
          // `params` whenever clientSideSearch is false — no change for existing
          // paginated columns.
          ...effectiveParams,
          limit: pagination.limit,
          offset: typeof pageParam === 'number' ? pageParam : 0,
        })
    : emptyAsync;

  const pagedQuery = useInfiniteQuery({
    queryKey: pagedQueryKey,
    queryFn: pagedQueryFn,
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      if (!lastPage || typeof lastPage !== 'object') {
        return undefined;
      }

      const page = lastPage as {
        nextOffset?: number | null;
        hasMore?: boolean;
      };
      if (page.hasMore && typeof page.nextOffset === 'number') {
        return page.nextOffset;
      }

      return undefined;
    },
    enabled: pagedQueryEnabled,
    staleTime: queryStaleTime,
    gcTime: queryCacheTime,
  });

  if (!filterOptions) {
    return {
      options: [],
      isLoading: false,
      isError: false,
    };
  }

  if (isDynamic) {
    // Transform data to FilterOption[]
    const rawData = pagination
      ? (pagedQuery.data?.pages ?? []).flatMap(page =>
          extractOptionItems(page as DynamicFilterOptionsResult)
        )
      : extractOptionItems(query.data as DynamicFilterOptionsResult);
    const labelFormatter =
      dynamicFilterOptions?.labelFormatter || ((v: string) => v);

    // Extract total from the first page (it's the same across all pages)
    const total = pagination
      ? (pagedQuery.data?.pages?.[0] as DynamicFilterOptionsPage | undefined)
          ?.total
      : undefined;

    if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
      return {
        options: [],
        total,
        isLoading: pagination ? pagedQuery.isLoading : query.isLoading,
        isError: pagination ? pagedQuery.isError : query.isError,
        refetch: pagination ? pagedQuery.refetch : query.refetch,
        hasMore: pagination ? pagedQuery.hasNextPage : undefined,
        isFetchingMore: pagination ? pagedQuery.isFetchingNextPage : undefined,
        onLoadMore: pagination ? pagedQuery.fetchNextPage : undefined,
        defaultRender: dynamicFilterOptions?.render,
        tooltipOptions: dynamicFilterOptions?.tooltip,
      };
    }

    // Check if data is array of strings or array of objects
    const transformedOptions: FilterOption[] = rawData.map(
      (item: string | DynamicFilterOptionItem) => {
        if (typeof item === 'string') {
          // Simple string values - use labelFormatter
          return {
            value: item,
            label: labelFormatter(item),
          };
        }

        // Object with metadata - preserve metadata and use render if available
        return {
          value: item.value,
          label: item.label || item.value,
          metadata: item.metadata,
        } as FilterOption;
      }
    );

    // KIR-5098: endpoint Matches tagged by kind list hostnames before IDs.
    const kindRank = (option: FilterOption): number =>
      option.metadata?.kind === 'id' ? 1 : 0;
    if (!dynamicFilterOptions?.preserveServerOrder) {
      transformedOptions.sort(
        (a, b) =>
          kindRank(a) - kindRank(b) ||
          (a.label ?? a.value).localeCompare(b.label ?? b.value, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
      );
    }

    // Dedupe by label only when an explicit labelFormatter is set.
    // Without a formatter, labels equal raw values (backend returns distinct keys),
    // and deduping would silently drop legitimate options that share a display name
    // (e.g. two org members with the same name in the Users filter).
    //
    // When labels collide (e.g. two wire types mapped to "Malicious Extension"),
    // merge ALL underlying values into metadata.mergedValues on the surviving
    // option so that ColumnFilterContent can expand them at selection time.
    const dedupedOptions = dynamicFilterOptions?.labelFormatter
      ? collapseOptionsByLabel(transformedOptions)
      : transformedOptions;

    // KIR-3769: client-side label search. The server returned the full option
    // set (search was stripped above); narrow it here by matching the typed
    // query against the formatted label, case-insensitively.
    const searchQuery = clientSideSearch ? params?.search?.trim() : undefined;
    const visibleOptions = searchQuery
      ? dedupedOptions.filter(option =>
          (option.label ?? option.value)
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        )
      : dedupedOptions;

    return {
      options: visibleOptions,
      // Merged labels are one option each, so the "N of M" total counts them
      // once too (the server counts raw values).
      total:
        total === undefined
          ? undefined
          : total - (transformedOptions.length - dedupedOptions.length),
      isLoading: pagination ? pagedQuery.isLoading : query.isLoading,
      isError: pagination ? pagedQuery.isError : query.isError,
      refetch: pagination ? pagedQuery.refetch : query.refetch,
      hasMore: pagination ? pagedQuery.hasNextPage : undefined,
      isFetchingMore: pagination ? pagedQuery.isFetchingNextPage : undefined,
      onLoadMore: pagination ? pagedQuery.fetchNextPage : undefined,
      defaultRender: dynamicFilterOptions?.render,
      tooltipOptions: dynamicFilterOptions?.tooltip,
    };
  }

  if (staticFilterOptions) {
    // Filter static options based on search query
    const searchQuery = params?.search;
    const filteredOptions = searchQuery
      ? staticFilterOptions.options.filter(option =>
          (option.label?.toLowerCase() || '').includes(
            searchQuery.toLowerCase()
          )
        )
      : staticFilterOptions.options;

    return {
      options: filteredOptions,
      isLoading: false,
      isError: false,
      defaultRender: staticFilterOptions.render,
      tooltipOptions: staticFilterOptions.tooltip,
    };
  }

  return {
    options: [],
    isLoading: false,
    isError: false,
  };
}
